import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Maximize2, RefreshCw } from "lucide-react";
import { API, api, getTokens } from "@/lib/api";
import { useApp } from "@/context/AppContext";

export default function CameraPlayer({ cam, big = false }) {
  const { t, staff } = useApp();
  const videoRef = useRef(null);
  const boxRef = useRef(null);
  const [state, setState] = useState("connecting");
  const [src, setSrc] = useState(null);
  const [tick, setTick] = useState(0);
  const [frame, setFrame] = useState(null);

  useEffect(() => {
    let dead = false;
    setState("connecting"); setSrc(null);
    api.get(`/cameras/${cam.id}/source`).then(({ data }) => { if (!dead) setSrc(data); }).catch(() => !dead && setState("error"));
    return () => { dead = true; };
  }, [cam.id, tick]);

  useEffect(() => {
    if (!src || src.kind !== "frames") return undefined;
    let dead = false, fails = 0;
    const tk = getTokens();
    const tickFn = async () => {
      try {
        const r = await fetch(`${API}/cameras/${cam.id}/frame.jpg?_=${Date.now()}`, { headers: { Authorization: `Bearer ${tk?.access_token}`, "X-Staff-Id": staff?.id || "" } });
        if (!r.ok) throw new Error();
        const b = await r.blob(); if (dead) return;
        setFrame((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(b); });
        fails = 0; setState("live");
      } catch { if (++fails >= 6) setState("error"); }
    };
    tickFn();
    const id = setInterval(tickFn, 500);
    return () => { dead = true; clearInterval(id); };
  }, [src, cam.id, staff?.id]);

  useEffect(() => {
    if (!src || src.kind === "mjpeg" || src.kind === "frames") return undefined;
    const video = videoRef.current;
    const url = src.proxied ? `${API}/cameras/${cam.id}/hls/index.m3u8` : src.url;
    let hls;
    if (Hls.isSupported()) {
      hls = new Hls({ lowLatencyMode: true, liveSyncDurationCount: 2, manifestLoadingMaxRetry: 4, manifestLoadingRetryDelay: 1500, xhrSetup: (xhr) => {
        if (!src.proxied) return;
        const tk = getTokens();
        if (tk) xhr.setRequestHeader("Authorization", `Bearer ${tk.access_token}`);
        if (staff?.id) xhr.setRequestHeader("X-Staff-Id", staff.id);
      } });
      hls.loadSource(url); hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => { video.play().catch(() => {}); setState("live"); });
      hls.on(Hls.Events.ERROR, (_, d) => { if (d.fatal) { console.warn("hls fatal", d.type, d.details, d.error?.message); setState("error"); hls.destroy(); } });
    } else if (video.canPlayType("application/vnd.apple.mpegurl") && !src.proxied) {
      video.src = url; video.play().then(() => setState("live")).catch(() => setState("error"));
    } else setState("error");
    return () => { hls?.destroy(); };
  }, [src, cam.id, staff?.id]);

  const full = () => boxRef.current?.requestFullscreen?.();
  return (
    <div ref={boxRef} className="relative bg-black rounded-xl overflow-hidden group aspect-video" data-testid={`cam-player-${cam.id}`}>
      {src?.kind === "frames" ? (
        frame ? <img src={frame} alt="" className="w-full h-full object-contain" /> : <div className="w-full h-full" />
      ) : src?.kind === "mjpeg" ? (
        <img src={src.url} alt="" className="w-full h-full object-contain" onLoad={() => setState("live")} onError={() => setState("error")} />
      ) : (
        <video ref={videoRef} muted playsInline autoPlay className="w-full h-full object-contain" />
      )}
      <div className="absolute top-2 start-2 flex items-center gap-2 text-white text-[11px] font-bold drop-shadow">
        <span className={`px-1.5 py-0.5 rounded ${state === "live" ? "bg-[#C7353B]" : "bg-black/60"}`} data-testid={`cam-state-${cam.id}`}>{state === "live" ? `● ${t("cam_live")}` : state === "error" ? t("cam_error") : t("cam_connecting")}</span>
        <span className={big ? "text-sm" : ""}>{cam.name}{cam.location ? ` · ${cam.location}` : ""}</span>
      </div>
      {state === "error" && <button type="button" onClick={() => setTick((x) => x + 1)} className="absolute inset-0 grid place-items-center text-white/90 text-sm gap-1" data-testid={`cam-retry-${cam.id}`}><span className="flex items-center gap-2 bg-black/60 px-3 py-1.5 rounded-lg"><RefreshCw className="w-4 h-4" />{t("cam_retry")}</span></button>}
      <button type="button" onClick={full} title={t("cam_fullscreen")} className="absolute bottom-2 end-2 w-8 h-8 rounded-lg bg-black/60 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`cam-full-${cam.id}`}><Maximize2 className="w-4 h-4" /></button>
    </div>
  );
}
